/**
 * ชิ้นส่วน UI ของ MFA ที่ใช้ร่วมกันทั้งหน้า Login (Desktop + เครื่องสแกน) และ panel ของผู้ใช้
 *
 * ความลับทั้งหมด (secret, otpauth URI, รหัสกู้คืน, mfa_token) อยู่ใน state ของ component
 * เท่านั้น — ไม่เขียนลง storage/cookie/console และถูกทิ้งเมื่อ component ถูกถอดออก
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { DeviceKind } from './client';
import './mfa.css';
import { MFA_CODE_LENGTH, describeMfaFailure } from './mfaMessages';
import type { MfaEnrolmentDone, MfaEnrolmentStart, MfaFailure, MfaResult } from './mfaClient';
import { primaryTouchTargetStyle, touchTargetStyle } from '../../scanner/touch';

interface StyleProps {
  readonly deviceKind: DeviceKind;
}

function fieldStyleOf(deviceKind: DeviceKind) {
  return deviceKind === 'HANDHELD' ? touchTargetStyle : undefined;
}

function primaryStyleOf(deviceKind: DeviceKind) {
  return deviceKind === 'HANDHELD' ? primaryTouchTargetStyle : undefined;
}

/** ช่องกรอก TOTP: แป้นตัวเลข + autofill one-time-code */
export function TotpInput({
  id,
  value,
  onChange,
  disabled,
  deviceKind,
}: StyleProps & {
  readonly id: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly disabled?: boolean;
}) {
  return (
    <input
      id={id}
      className="cap-mfa__input cap-mfa__input--code"
      style={fieldStyleOf(deviceKind)}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="one-time-code"
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      maxLength={MFA_CODE_LENGTH}
      value={value}
      disabled={disabled}
      data-testid="mfa-code-input"
      onChange={(event) => {
        onChange(event.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH));
      }}
    />
  );
}

/** ขั้นกรอกรหัสหลัง login: TOTP หรือรหัสกู้คืน */
export function MfaCodeStep({
  deviceKind,
  busy,
  error,
  onSubmit,
  onRestart,
}: StyleProps & {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (input: { readonly code?: string; readonly recoveryCode?: string }) => void;
  readonly onRestart: () => void;
}) {
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    if (useRecovery) {
      const value = recovery.trim();
      if (value !== '') {
        onSubmit({ recoveryCode: value });
      }
    } else if (code.length === MFA_CODE_LENGTH) {
      onSubmit({ code });
    }
    setCode('');
    setRecovery('');
  };

  const ready = useRecovery ? recovery.trim() !== '' : code.length === MFA_CODE_LENGTH;

  return (
    <div className="cap-mfa" data-testid="mfa-code-step">
      <h1 className="cap-mfa__title">ยืนยันตัวตนสองขั้น</h1>
      <p className="cap-mfa__sub">
        {useRecovery
          ? 'กรอกรหัสกู้คืนหนึ่งรหัสจากชุดที่บันทึกไว้ (ใช้ได้ครั้งเดียว)'
          : 'เปิดแอปยืนยันตัวตนในโทรศัพท์แล้วกรอกรหัส 6 หลักที่แสดงอยู่'}
      </p>
      <form className="cap-mfa__form" method="post" onSubmit={submit} aria-label="ยืนยันตัวตนสองขั้น">
        <div className="cap-mfa__field">
          {useRecovery ? (
            <>
              <label className="cap-mfa__label" htmlFor="cap-mfa-recovery">
                รหัสกู้คืน
              </label>
              <input
                id="cap-mfa-recovery"
                className="cap-mfa__input"
                style={fieldStyleOf(deviceKind)}
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={recovery}
                disabled={busy}
                data-testid="mfa-recovery-input"
                onChange={(event) => {
                  setRecovery(event.target.value);
                }}
              />
            </>
          ) : (
            <>
              <label className="cap-mfa__label" htmlFor="cap-mfa-code">
                รหัส 6 หลัก
              </label>
              <TotpInput id="cap-mfa-code" value={code} onChange={setCode} disabled={busy} deviceKind={deviceKind} />
            </>
          )}
        </div>

        {error === null ? null : (
          <p className="cap-mfa__error" role="alert" data-testid="mfa-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="cap-mfa__primary"
          style={primaryStyleOf(deviceKind)}
          disabled={busy || !ready}
          data-testid="mfa-submit"
        >
          {busy ? 'กำลังตรวจสอบ…' : 'ยืนยัน'}
        </button>
        <button
          type="button"
          className="cap-mfa__link"
          style={fieldStyleOf(deviceKind)}
          disabled={busy}
          data-testid="mfa-toggle-recovery"
          onClick={() => {
            setUseRecovery((value) => !value);
            setCode('');
            setRecovery('');
          }}
        >
          {useRecovery ? 'ใช้รหัส 6 หลักจากแอปแทน' : 'ใช้รหัสกู้คืนแทน'}
        </button>
        <button
          type="button"
          className="cap-mfa__link"
          style={fieldStyleOf(deviceKind)}
          data-testid="mfa-restart"
          onClick={onRestart}
        >
          เริ่มเข้าสู่ระบบใหม่
        </button>
      </form>
    </div>
  );
}

/** QR ที่เซิร์ฟเวอร์สร้างเอง — เรนเดอร์เป็น <img> เท่านั้น (สคริปต์ใน SVG ไม่ทำงาน) ไม่ inject markup เข้า DOM */
export function QrImage({ svg }: { readonly svg: string }) {
  if (!svg.trimStart().startsWith('<svg')) {
    return null;
  }
  return (
    <img
      className="cap-mfa__qr"
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`}
      alt="QR code สำหรับเพิ่มบัญชีในแอปยืนยันตัวตน"
      width={200}
      height={200}
      data-testid="mfa-qr"
    />
  );
}

/** แสดงรหัสกู้คืนครั้งเดียว — ต้องกดยืนยันว่าบันทึกแล้วจึงไปต่อได้ */
export function RecoveryCodesSheet({
  codes,
  deviceKind,
  onAcknowledged,
  continueLabel = 'ฉันบันทึกรหัสกู้คืนไว้แล้ว — ดำเนินการต่อ',
}: StyleProps & {
  readonly codes: readonly string[];
  readonly onAcknowledged: () => void;
  readonly continueLabel?: string;
}) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = () => {
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      setCopyState('failed');
      return;
    }
    clipboard.writeText(codes.join('\n')).then(
      () => {
        setCopyState('copied');
      },
      () => {
        setCopyState('failed');
      },
    );
  };

  return (
    <div className="cap-mfa" data-testid="mfa-recovery-codes">
      <h2 className="cap-mfa__title">รหัสกู้คืน</h2>
      <p className="cap-mfa__notice" role="alert">
        เก็บรหัสเหล่านี้ไว้ในที่ปลอดภัย — <strong>แสดงครั้งเดียวเท่านั้น</strong> แต่ละรหัสใช้ได้ครั้งเดียว
        ใช้เมื่อโทรศัพท์หายหรือเข้าแอปยืนยันตัวตนไม่ได้ ถ้าทำหายต้องสร้างชุดใหม่
      </p>
      <ul className="cap-mfa__codes">
        {codes.map((code) => (
          <li key={code} className="cap-mfa__code" data-testid="mfa-recovery-code">
            {code}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="cap-mfa__secondary"
        style={fieldStyleOf(deviceKind)}
        data-testid="mfa-copy-codes"
        onClick={copy}
      >
        คัดลอกทั้งหมด
      </button>
      {copyState === 'copied' ? (
        <p className="cap-mfa__ok" role="status" data-testid="mfa-copy-status">
          คัดลอกแล้ว
        </p>
      ) : null}
      {copyState === 'failed' ? (
        <p className="cap-mfa__error" role="alert" data-testid="mfa-copy-status">
          คัดลอกอัตโนมัติไม่ได้ — จดรหัสด้วยตนเอง
        </p>
      ) : null}
      <button
        type="button"
        className="cap-mfa__primary"
        style={primaryStyleOf(deviceKind)}
        data-testid="mfa-codes-saved"
        onClick={onAcknowledged}
      >
        {continueLabel}
      </button>
    </div>
  );
}

type EnrolPhase =
  | { readonly kind: 'starting' }
  | { readonly kind: 'start-failed'; readonly failure: MfaFailure }
  | { readonly kind: 'scan'; readonly start: MfaEnrolmentStart }
  | { readonly kind: 'codes'; readonly codes: readonly string[] };

/** ลำดับ: ขอ secret → แสดง QR + กรอกรหัสยืนยัน → แสดงรหัสกู้คืนครั้งเดียว → onFinished */
export function MfaEnrolFlow({
  deviceKind,
  start,
  confirm,
  onFinished,
  onCancel,
  onRestart,
}: StyleProps & {
  readonly start: () => Promise<MfaResult<MfaEnrolmentStart>>;
  readonly confirm: (code: string) => Promise<MfaResult<MfaEnrolmentDone>>;
  readonly onFinished: () => void;
  readonly onCancel?: () => void;
  /** มีเฉพาะฝั่ง login: challenge ใช้ไม่ได้แล้วต้องเริ่มใหม่ */
  readonly onRestart?: () => void;
}) {
  const [phase, setPhase] = useState<EnrolPhase>({ kind: 'starting' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);
  const startRef = useRef(start);
  startRef.current = start;

  useEffect(() => {
    // เริ่มแค่ครั้งเดียวต่อ mount: การเรียก start สองครั้งจะสร้าง secret สองชุดและชุดที่โชว์อาจไม่ใช่ชุดที่เก็บ
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    void startRef.current().then((result) => {
      setPhase(
        result.status === 'ok'
          ? { kind: 'scan', start: result.value }
          : { kind: 'start-failed', failure: result.failure },
      );
    });
  }, []);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || code.length !== MFA_CODE_LENGTH) {
      return;
    }
    const entered = code;
    setBusy(true);
    setError(null);
    void confirm(entered).then((result) => {
      setBusy(false);
      setCode('');
      if (result.status === 'ok') {
        // ทิ้ง secret/QR ทันที เหลือแค่รหัสกู้คืนที่ต้องโชว์
        setPhase({ kind: 'codes', codes: result.value.recoveryCodes });
        return;
      }
      setError(describeMfaFailure(result.failure));
    });
  };

  if (phase.kind === 'starting') {
    return (
      <p className="cap-mfa__sub" role="status" data-testid="mfa-enrol-loading">
        กำลังเตรียมการตั้งค่า…
      </p>
    );
  }

  if (phase.kind === 'start-failed') {
    return (
      <div className="cap-mfa" data-testid="mfa-enrol-failed">
        <p className="cap-mfa__error" role="alert" data-testid="mfa-error">
          {describeMfaFailure(phase.failure)}
        </p>
        {onRestart === undefined ? null : (
          <button
            type="button"
            className="cap-mfa__link"
            style={fieldStyleOf(deviceKind)}
            data-testid="mfa-restart"
            onClick={onRestart}
          >
            เริ่มเข้าสู่ระบบใหม่
          </button>
        )}
        {onCancel === undefined ? null : (
          <button
            type="button"
            className="cap-mfa__link"
            style={fieldStyleOf(deviceKind)}
            data-testid="mfa-cancel"
            onClick={onCancel}
          >
            ปิด
          </button>
        )}
      </div>
    );
  }

  if (phase.kind === 'codes') {
    return <RecoveryCodesSheet codes={phase.codes} deviceKind={deviceKind} onAcknowledged={onFinished} />;
  }

  return (
    <div className="cap-mfa" data-testid="mfa-enrol-scan">
      <h1 className="cap-mfa__title">ตั้งค่าการยืนยันตัวตนสองขั้น</h1>
      <p className="cap-mfa__sub">
        สแกน QR ด้วยแอปยืนยันตัวตน (เช่น Google Authenticator) หรือกรอกรหัสลับด้วยตนเอง แล้วกรอกรหัส 6 หลักที่แอปแสดง
      </p>
      <QrImage svg={phase.start.qrSvg} />
      <div className="cap-mfa__manual">
        <span className="cap-mfa__label">รหัสลับ (กรอกเอง)</span>
        <code className="cap-mfa__secret" data-testid="mfa-secret">
          {phase.start.secret}
        </code>
      </div>
      <form className="cap-mfa__form" method="post" onSubmit={submit} aria-label="ยืนยันการตั้งค่า MFA">
        <div className="cap-mfa__field">
          <label className="cap-mfa__label" htmlFor="cap-mfa-enrol-code">
            รหัส 6 หลักจากแอป
          </label>
          <TotpInput id="cap-mfa-enrol-code" value={code} onChange={setCode} disabled={busy} deviceKind={deviceKind} />
        </div>
        {error === null ? null : (
          <p className="cap-mfa__error" role="alert" data-testid="mfa-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="cap-mfa__primary"
          style={primaryStyleOf(deviceKind)}
          disabled={busy || code.length !== MFA_CODE_LENGTH}
          data-testid="mfa-confirm"
        >
          {busy ? 'กำลังตรวจสอบ…' : 'ยืนยันและเปิดใช้งาน'}
        </button>
        {onRestart === undefined ? null : (
          <button
            type="button"
            className="cap-mfa__link"
            style={fieldStyleOf(deviceKind)}
            data-testid="mfa-restart"
            onClick={onRestart}
          >
            เริ่มเข้าสู่ระบบใหม่
          </button>
        )}
        {onCancel === undefined ? null : (
          <button
            type="button"
            className="cap-mfa__link"
            style={fieldStyleOf(deviceKind)}
            data-testid="mfa-cancel"
            onClick={onCancel}
          >
            ยกเลิก
          </button>
        )}
      </form>
    </div>
  );
}
